import React, { useState } from 'react';
import useGameStore from '../store/gameStore';
import { getSecteur, getFournisseursBySecteur, remisePourQty, prixUnitaireNet, couvertureJours, SEUIL_SURSTOCK_JOURS, getRelationFournisseur, relationNiveau, fournisseurCouvre } from '../utils/retail';
import { formatMoney } from '../utils/format';
import { Boxes, Truck, AlertTriangle } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import ReaproAutoPanel from '../components/achats/ReaproAutoPanel';

/**
 * SCD Lot 1 — Écran « Achats & stock ».
 * Stock par famille (couverture, rupture/surstock), jauge de réserve,
 * commande fournisseur (délai, remise volume, paiement 30/60 j),
 * commandes en cours et échéances à payer.
 */
export default function Achats() {
  const retail = useGameStore(s => s.retail);
  const totalDays = useGameStore(s => s.totalDays);
  const passerCommande = useGameStore(s => s.passerCommande);

  const [fournisseurId, setFournisseurId] = useState('');
  const [famId, setFamId] = useState('');
  const [qty, setQty] = useState('');

  if (!retail) {
    return <div className="text-slate-400 text-sm" data-testid="achats-empty">Aucun magasin actif.</div>;
  }
  const sec = getSecteur(retail.secteur);
  const fournisseurs = getFournisseursBySecteur(retail.secteur);
  const fournisseur = fournisseurs.find(f => f.id === fournisseurId) || null;
  const fam = sec.familles.find(f => f.id === famId) || null;
  const qtyNum = parseInt(qty, 10) || 0;
  const relation = fournisseur ? getRelationFournisseur(retail, fournisseur.id) : 50;
  const niveau = relationNiveau(relation);
  const famillesDispo = fournisseur ? sec.familles.filter(f => fournisseurCouvre(fournisseur, f.id)) : sec.familles;

  const stockTotal = Object.values(retail.stock).reduce((a, x) => a + x.qty, 0);
  const enCours = retail.commandes.filter(c => !c.recu).reduce((a, c) => a + c.qty, 0);
  const capacite = retail.magasin.capaciteReserve;
  const reservePct = Math.min(100, Math.round(((stockTotal + enCours) / capacite) * 100));

  const remise = fournisseur && qtyNum > 0 ? remisePourQty(fournisseur, qtyNum, relation) : 0;
  const pu = fournisseur && fam && qtyNum > 0 ? prixUnitaireNet(fam, fournisseur, qtyNum, retail.modele, relation) : 0;
  const montant = Math.round(pu * qtyNum * 100) / 100;
  const delaiEffectif = fournisseur ? Math.max(1, (fournisseur.delai_livraison_jours || 3) - niveau.delaiBonus) : 0;

  const commandesEnCours = retail.commandes.filter(c => !c.recu);
  const echeances = retail.commandes.filter(c => c.recu && !c.paye);

  const handleCommande = () => {
    if (!fournisseur || !fam || qtyNum <= 0) return;
    const res = passerCommande({ fournisseur, famId: fam.id, qty: qtyNum });
    if (res?.ok) {
      toast('Commande passée', {
        description: `${qtyNum} × ${fam.nom} chez ${fournisseur.nom} — livraison J${res.commande.jourLivraison}, paiement à ${fournisseur.paiement_jours} j (${formatMoney(montant)}).`,
      });
      setQty('');
    } else if (res?.reason === 'gamme') {
      toast.error('Gamme non couverte', {
        description: `${fournisseur.nom} ne distribue pas la gamme ${fam.nom}.`,
      });
    } else if (res?.reason === 'capacite') {
      toast.error('Réserve pleine', {
        description: `Capacité restante : ${res.restant} unités (stock + commandes en cours).`,
      });
    }
  };

  return (
    <div className="space-y-5" data-testid="achats-page">
      <div>
        <p className="overline">{sec.nom} · Réserve {stockTotal} / {capacite} unités</p>
        <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground mt-0.5 flex items-center gap-2">
          <Boxes className="w-6 h-6 text-accent" /> Achats & stock
        </h1>
      </div>

      {/* Jauge réserve */}
      <div className="surface-card p-4" data-testid="reserve-gauge">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="uppercase tracking-wider text-slate-500 font-bold text-[10px]">Capacité de réserve</span>
          <span className="font-mono text-slate-300">{stockTotal} en stock + {enCours} en livraison — {reservePct} %</span>
        </div>
        <div className="h-2 rounded-full bg-ink-700 overflow-hidden">
          <div className={`h-full rounded-full ${reservePct > 90 ? 'bg-red-500' : reservePct > 70 ? 'bg-amber-500' : 'bg-accent'}`} style={{ width: `${reservePct}%` }} />
        </div>
      </div>

      {/* Stock par famille */}
      <div className="surface-card overflow-x-auto">
        <div className="px-4 py-3 border-b border-ink-600">
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">Stock par famille</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-600 text-left">
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500">Famille</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 text-right">Unités</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 text-right">Valeur (CUMP)</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 text-right">Ventes/j (7 j)</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 text-right">Couverture</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 text-right">Statut</th>
            </tr>
          </thead>
          <tbody>
            {sec.familles.map(f => {
              const st = retail.stock[f.id] || { qty: 0, cump: f.prix_achat };
              const v7 = retail.ventes7?.[f.id] || [];
              const avg = v7.length ? v7.reduce((a, b) => a + b, 0) / v7.length : 0;
              const couv = couvertureJours(st.qty, v7);
              const rupture = st.qty === 0;
              const surstock = couv > SEUIL_SURSTOCK_JOURS && couv !== 999;
              return (
                <tr key={f.id} className="border-b border-ink-700/60" data-testid={`stock-row-${f.id}`}>
                  <td className="px-3 py-2 font-semibold text-slate-100">{f.nom}</td>
                  <td className={`px-3 py-2 text-right font-mono ${rupture ? 'text-red-400 font-bold' : 'text-slate-200'}`}>{st.qty}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-300">{formatMoney(Math.round(st.qty * st.cump))}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-400">{avg.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-300">{couv === 999 ? '—' : `${couv} j`}</td>
                  <td className="px-3 py-2 text-right">
                    {rupture && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-red-500/15 text-red-400">Rupture</span>}
                    {surstock && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-amber-500/15 text-amber-400">Surstock</span>}
                    {!rupture && !surstock && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400">OK</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Commande fournisseur */}
      <div className="surface-card p-4 space-y-4" data-testid="commande-form">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase flex items-center gap-2">
          <Truck className="w-4 h-4 text-accent" /> Passer une commande
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {fournisseurs.map(f => {
            const rel = getRelationFournisseur(retail, f.id);
            const niv = relationNiveau(rel);
            const coef = f.prix_coef || 1;
            const coefLabel = coef > 1 ? `+${Math.round((coef - 1) * 100)} %` : coef < 1 ? `−${Math.round((1 - coef) * 100)} %` : 'Prix std';
            const gammes = (f.familles || []).map(id => sec.familles.find(x => x.id === id)?.nom).filter(Boolean);
            return (
            <button
              key={f.id}
              type="button"
              onClick={() => { setFournisseurId(f.id); if (famId && !fournisseurCouvre(f, famId)) setFamId(''); }}
              className={`text-left rounded-lg border p-3 transition-all ${fournisseurId === f.id ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-ink-600 bg-ink-900/40 hover:border-ink-500'}`}
              data-testid={`fournisseur-${f.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm text-slate-100">{f.nom}</span>
                <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${coef > 1 ? 'bg-rose-500/15 text-rose-300' : coef < 1 ? 'bg-emerald-500/15 text-emerald-300' : 'bg-ink-700 text-slate-400'}`}>{coefLabel}</span>
              </div>
              <p className="text-[10px] uppercase font-mono text-slate-500 mt-0.5">{f.profil}</p>
              <p className="text-[11px] text-slate-300 mt-1">
                {gammes.length >= sec.familles.length ? 'Toutes les gammes' : gammes.join(' · ')}
              </p>
              <p className="text-[11px] text-slate-400 mt-1 font-mono">
                Livraison {f.delai_livraison_jours} j{rel >= 70 ? ' (−1 j partenaire)' : ''} · Paiement à {f.paiement_jours} j · Fiabilité {f.fiabilite_pct} %
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5 font-mono">
                {rel < 30 ? 'Remises volume suspendues (relation tendue)' : `Remises : ${f.remise_volume.map(r => `${r.min_qty}+ → −${r.remise_pct} %`).join(' · ')}`}
              </p>
              <div className="mt-2" data-testid={`relation-${f.id}`}>
                <div className="flex items-center justify-between text-[10px] mb-0.5">
                  <span className="uppercase tracking-wider text-slate-500 font-bold">Relation</span>
                  <span className={`font-mono font-bold ${niv.id === 'partenaire' ? 'text-emerald-300' : niv.id === 'tendue' ? 'text-rose-300' : 'text-slate-300'}`}>{Math.round(rel)}/100 · {niv.label}{niv.id === 'partenaire' ? ' (fidélité −3 %)' : ''}</span>
                </div>
                <div className="h-1.5 rounded-full bg-ink-700 overflow-hidden">
                  <div className={`h-full rounded-full ${niv.id === 'partenaire' ? 'bg-emerald-400' : niv.id === 'tendue' ? 'bg-rose-400' : 'bg-slate-400'}`} style={{ width: `${rel}%` }} />
                </div>
              </div>
            </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Famille</label>
            <select
              value={famId}
              onChange={e => setFamId(e.target.value)}
              className="bg-ink-900 border border-ink-600 rounded px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-accent"
              data-testid="select-famille"
            >
              <option value="">— Choisir —</option>
              {famillesDispo.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Quantité</label>
            <input
              type="number"
              min="1"
              value={qty}
              onChange={e => setQty(e.target.value)}
              className="w-28 bg-ink-900 border border-ink-600 rounded px-3 py-2 text-sm font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-accent"
              data-testid="input-qty"
            />
          </div>
          <button
            type="button"
            onClick={handleCommande}
            disabled={!fournisseur || !fam || qtyNum <= 0}
            className="bg-accent text-white px-5 py-2 rounded-lg text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-accent-hover transition-colors"
            data-testid="btn-commander"
          >
            Commander
          </button>
          {fournisseur && fam && qtyNum > 0 && (
            <div className="text-xs font-mono text-slate-300 bg-ink-900/60 border border-ink-600 rounded px-3 py-2" data-testid="apercu-commande">
              PU net : <strong>{pu.toFixed(2)} €</strong>{remise > 0 && <span className="text-emerald-400"> (−{remise} % volume)</span>}{niveau.fidelitePct > 0 && <span className="text-emerald-400"> (−{niveau.fidelitePct} % fidélité)</span>}
              {' '}· Total : <strong>{formatMoney(montant)}</strong>
              {' '}· Livraison J{totalDays + delaiEffectif}
              {' '}· Paiement J{totalDays + delaiEffectif + fournisseur.paiement_jours}
            </div>
          )}
        </div>
      </div>

      {/* 02/07 — Réapprovisionnement automatique */}
      <ReaproAutoPanel />

      {/* Commandes en cours + échéances */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface-card p-4" data-testid="commandes-en-cours">
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3">À livrer ({commandesEnCours.length})</h3>
          {commandesEnCours.length === 0 && <p className="text-xs text-slate-500">Aucune commande en cours.</p>}
          <div className="space-y-2">
            {commandesEnCours.map(c => (
              <div key={c.id} className="flex items-center justify-between text-xs bg-ink-900/50 border border-ink-700 rounded px-3 py-2">
                <span className="text-slate-200">{c.qty} × {c.famNom} <span className="text-slate-500">· {c.fournisseurNom}</span></span>
                <span className="font-mono text-slate-400">Livraison J{c.jourLivraison}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="surface-card p-4" data-testid="echeances">
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Échéances à payer ({echeances.length})
          </h3>
          {echeances.length === 0 && <p className="text-xs text-slate-500">Aucune facture fournisseur en attente.</p>}
          <div className="space-y-2">
            {echeances.map(c => (
              <div key={c.id} className="flex items-center justify-between text-xs bg-ink-900/50 border border-ink-700 rounded px-3 py-2">
                <span className="text-slate-200">{c.fournisseurNom} <span className="text-slate-500">· {c.qty} × {c.famNom}</span></span>
                <span className="font-mono text-amber-300">{formatMoney(c.montant)} — J{c.jourEcheance}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
